/**
 * Módulo de autenticación - Dominio compartido
 *
 * Contiene casos de uso y lógica de negocio para autenticación,
 * independiente del framework de UI.
 */

import { AuthService } from '@pepi/api-client'
import type {
  LoginRequest,
  RegisterRequest,
  UserResponse,
  FamilyResponse,
  MemberResponse
} from '@pepi/api-client'
import {
  getStorage,
  isStorageRegistered,
  getSecureStorage,
  isSecureStorageRegistered
} from '../../ports'

// ============== CONSTANTES ==============

export const AUTH_STORAGE_KEYS = {
  TOKEN: 'pepi_token',
  REFRESH_TOKEN: 'pepi_refresh_token',
  USER: 'pepi_user',
  FAMILY: 'pepi_family',
  /**
   * Ficha de miembro del propio usuario. El backend la devuelve en
   * login/register y es el ÚNICO sitio donde viaja su nombre: `UserResponse`
   * solo trae id, email y flags.
   */
  MEMBER: 'pepi_member'
} as const

// ============== TIPOS ==============

export interface AuthState {
  user: UserResponse | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  loading: boolean
}

export interface AuthCredentials {
  token: string
  refreshToken: string | null
  user: UserResponse
  /** Familia activa del usuario, devuelta por el backend en login/register. */
  family: FamilyResponse | null
  /**
   * Ficha de miembro del propio usuario, devuelta por el backend en
   * login/register. Es donde viaja su NOMBRE: `UserResponse` no tiene campo
   * `name` — solo id, email y flags. Sin esto, la app no sabe cómo se llama
   * quien ha entrado.
   */
  member: MemberResponse | null
}

// ============== CASOS DE USO ==============

/**
 * Caso de uso: Iniciar sesión
 */
export async function loginUseCase(credentials: LoginRequest): Promise<AuthCredentials> {
  const response = await AuthService.login(credentials)

  const authCredentials: AuthCredentials = {
    token: response.token!,
    refreshToken: response.refreshToken ?? null,
    user: response.user!,
    family: response.family ?? null,
    member: response.member ?? null
  }

  // Persistir credenciales
  await persistCredentials(authCredentials)

  return authCredentials
}

/**
 * Caso de uso: Registrar usuario
 */
export async function registerUseCase(userData: RegisterRequest): Promise<AuthCredentials> {
  const response = await AuthService.register(userData)

  const authCredentials: AuthCredentials = {
    token: response.token!,
    refreshToken: response.refreshToken ?? null,
    user: response.user!,
    family: response.family ?? null,
    member: response.member ?? null
  }

  // Persistir credenciales
  await persistCredentials(authCredentials)

  return authCredentials
}

/**
 * Caso de uso: Cerrar sesión
 */
export async function logoutUseCase(): Promise<void> {
  try {
    await AuthService.logout()
  } catch (error) {
    console.warn('Error during logout API call:', error)
  } finally {
    await clearPersistedCredentials()
  }
}

/**
 * Caso de uso: Cargar sesión desde almacenamiento
 */
export async function loadSessionUseCase(): Promise<AuthCredentials | null> {
  const credentials = await loadPersistedCredentials()

  if (!credentials) {
    return null
  }

  // Validar que el token sigue siendo válido
  if (!isTokenValid(credentials.token)) {
    await clearPersistedCredentials()
    return null
  }

  return credentials
}

/**
 * Caso de uso: Cargar la familia activa persistida (si existe).
 *
 * Útil al rehidratar la sesión: el backend devuelve la familia en
 * login/register y aquí la recuperamos para inicializar el estado
 * sin tener que volver a pedirla.
 */
export async function loadPersistedFamily(): Promise<FamilyResponse | null> {
  if (!isStorageRegistered()) return null

  const storage = getStorage()
  // Mismo doble parseo que en `loadPersistedCredentials`: ver `deserializar`.
  return deserializar<FamilyResponse>(await storage.get(AUTH_STORAGE_KEYS.FAMILY))
}

/**
 * Caso de uso: cargar la ficha de miembro persistida (si existe).
 *
 * Simétrico de {@link loadPersistedFamily}, y hace falta por lo mismo:
 * `apps/web` rehidrata por una vía rápida que lee del storage del api-client y
 * SALE ANTES de llegar a `loadPersistedCredentials`. Sin esto, al recargar la
 * web se perdía el nombre del usuario aunque la sesión siguiera puesta.
 */
export async function loadPersistedMember(): Promise<MemberResponse | null> {
  if (!isStorageRegistered()) return null

  const storage = getStorage()
  return deserializar<MemberResponse>(await storage.get(AUTH_STORAGE_KEYS.MEMBER))
}

/**
 * Caso de uso: Actualizar información del usuario actual
 */
export async function refreshUserUseCase(): Promise<UserResponse> {
  const user = await AuthService.getCurrentUser()

  // Actualizar usuario en almacenamiento
  if (isStorageRegistered()) {
    const storage = getStorage()
    await storage.set(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
  }

  return user
}

// ============== VALIDADORES ==============

/**
 * Valida si un token JWT es válido (no expirado)
 */
export function isTokenValid(token: string): boolean {
  return AuthService.isTokenValid(token)
}

/**
 * Obtiene información del token decodificado
 */
export function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const payload = JSON.parse(atob(parts[1]))
    return payload
  } catch {
    return null
  }
}

/**
 * Obtiene el tiempo restante del token en segundos
 */
export function getTokenTimeRemaining(token: string): number {
  const payload = decodeToken(token)
  if (!payload || typeof payload.exp !== 'number') return 0

  const now = Math.floor(Date.now() / 1000)
  return Math.max(0, payload.exp - now)
}

// ============== PERSISTENCIA ==============

/**
 * Persiste las credenciales de autenticación
 */
async function persistCredentials(credentials: AuthCredentials): Promise<void> {
  // Usar SecureStorage si está disponible, sino Storage normal
  if (isSecureStorageRegistered()) {
    const secureStorage = getSecureStorage()
    await secureStorage.setSecure(AUTH_STORAGE_KEYS.TOKEN, credentials.token)
    if (credentials.refreshToken) {
      await secureStorage.setSecure(AUTH_STORAGE_KEYS.REFRESH_TOKEN, credentials.refreshToken)
    }
  } else if (isStorageRegistered()) {
    const storage = getStorage()
    await storage.set(AUTH_STORAGE_KEYS.TOKEN, credentials.token)
    if (credentials.refreshToken) {
      await storage.set(AUTH_STORAGE_KEYS.REFRESH_TOKEN, credentials.refreshToken)
    }
  }

  // Usuario y familia activa siempre en storage normal (no son sensibles)
  if (isStorageRegistered()) {
    const storage = getStorage()
    await storage.set(AUTH_STORAGE_KEYS.USER, JSON.stringify(credentials.user))
    if (credentials.family) {
      await storage.set(AUTH_STORAGE_KEYS.FAMILY, JSON.stringify(credentials.family))
    } else {
      await storage.remove(AUTH_STORAGE_KEYS.FAMILY)
    }
    if (credentials.member) {
      await storage.set(AUTH_STORAGE_KEYS.MEMBER, JSON.stringify(credentials.member))
    } else {
      await storage.remove(AUTH_STORAGE_KEYS.MEMBER)
    }
  }
}

/**
 * Carga las credenciales persistidas
 */
/**
 * Devuelve el objeto guardado, venga ya parseado o como cadena JSON.
 *
 * BUG QUE ESTO CIERRA (encontrado el 2026-07-29 por
 * `frontend/scripts/check-view-contrast.mjs`, la primera vez que algo recargó
 * la app con sesión): esto hacía `JSON.parse(userJson)` a secas, pero los DOS
 * adaptadores de storage (web y mobile) YA hacen `JSON.parse` dentro de su
 * `get()`. O sea que aquí llegaba un OBJETO y se volvía a parsear:
 * `JSON.parse({})` estringa a "[object Object]" y revienta. El `catch` lo
 * dejaba en `null`, `loadSessionUseCase` devolvía `null` y la app **cerraba la
 * sesión y mandaba al login CADA VEZ que se reabría**. Afectaba a web y a
 * mobile por igual.
 *
 * No lo detectó ningún test porque ninguno ejercitaba la rehidratación
 * completa: los tests de `auth` mockean `@pepi/api-client`, no el puerto de
 * storage con un adaptador real.
 *
 * Se acepta la cadena además del objeto a propósito: hay sesiones ya guardadas
 * ahí fuera y un adaptador podría no parsear.
 */
function deserializar<T>(valor: unknown): T | null {
  if (valor == null) return null
  if (typeof valor === 'string') {
    try {
      return JSON.parse(valor) as T
    } catch {
      return null
    }
  }
  return valor as T
}

async function loadPersistedCredentials(): Promise<AuthCredentials | null> {
  let token: string | null = null
  let refreshToken: string | null = null
  let user: UserResponse | null = null
  let family: FamilyResponse | null = null
  let member: MemberResponse | null = null

  // Cargar token
  if (isSecureStorageRegistered()) {
    const secureStorage = getSecureStorage()
    token = await secureStorage.getSecure(AUTH_STORAGE_KEYS.TOKEN)
    refreshToken = await secureStorage.getSecure(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  } else if (isStorageRegistered()) {
    const storage = getStorage()
    token = await storage.get<string>(AUTH_STORAGE_KEYS.TOKEN)
    refreshToken = await storage.get<string>(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  }

  // Cargar usuario, familia activa y ficha de miembro
  if (isStorageRegistered()) {
    const storage = getStorage()
    user = deserializar<UserResponse>(await storage.get(AUTH_STORAGE_KEYS.USER))
    family = deserializar<FamilyResponse>(await storage.get(AUTH_STORAGE_KEYS.FAMILY))
    member = deserializar<MemberResponse>(await storage.get(AUTH_STORAGE_KEYS.MEMBER))
  }

  if (!token || !user) {
    return null
  }

  return { token, refreshToken, user, family, member }
}

/**
 * Limpia las credenciales persistidas
 */
async function clearPersistedCredentials(): Promise<void> {
  if (isSecureStorageRegistered()) {
    const secureStorage = getSecureStorage()
    await secureStorage.removeSecure(AUTH_STORAGE_KEYS.TOKEN)
    await secureStorage.removeSecure(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  }

  if (isStorageRegistered()) {
    const storage = getStorage()
    await storage.remove(AUTH_STORAGE_KEYS.TOKEN)
    await storage.remove(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
    await storage.remove(AUTH_STORAGE_KEYS.USER)
    await storage.remove(AUTH_STORAGE_KEYS.FAMILY)
    await storage.remove(AUTH_STORAGE_KEYS.MEMBER)
  }
}

// ============== VALIDACIÓN DEL ALTA ==============

/**
 * Errores del formulario de alta, por campo. Vacío = se puede enviar.
 */
export interface RegistrationErrors {
  firstName?: string
  lastName?: string
  email?: string
  password?: string
}

export interface RegistrationInput {
  firstName?: string
  lastName?: string
  email?: string
  password?: string
}

/**
 * Valida un alta ANTES de mandarla, con las MISMAS reglas que el backend.
 *
 * Espejo literal de las anotaciones de `RegisterRequest.java`:
 *   - `firstName` y `lastName`: `@NotBlank` + `@Size(min = 2, max = 50)`
 *   - `password`: `@Size(min = 8)` + `@Pattern("^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$")`
 *   - `email`: `@NotBlank` + `@Email` + `@Size(max = 255)`
 *
 * Vive aquí y no en una pantalla porque las dos apts piden lo mismo y la regla
 * es del servidor, no de la UI: duplicada, una de las dos se desvía y el
 * usuario se come un 400 con un mensaje genérico.
 *
 * Los mensajes son los del backend, a propósito: si una validación se escapa,
 * el usuario lee la misma frase venga de donde venga.
 */
export function validateRegistration(input: RegistrationInput): RegistrationErrors {
  const errors: RegistrationErrors = {}

  const firstName = input.firstName?.trim() ?? ''
  if (!firstName) {
    errors.firstName = 'El nombre es requerido'
  } else if (firstName.length < 2 || firstName.length > 50) {
    errors.firstName = 'El nombre debe tener entre 2 y 50 caracteres'
  }

  const lastName = input.lastName?.trim() ?? ''
  if (!lastName) {
    errors.lastName = 'El apellido es requerido'
  } else if (lastName.length < 2 || lastName.length > 50) {
    errors.lastName = 'El apellido debe tener entre 2 y 50 caracteres'
  }

  const email = input.email?.trim() ?? ''
  if (!email) {
    errors.email = 'El email es requerido'
  } else if (!/^\S+@\S+\.\S+$/.test(email)) {
    errors.email = 'El email no es válido'
  } else if (email.length > 255) {
    errors.email = 'El email no puede exceder 255 caracteres'
  }

  const password = input.password ?? ''
  if (!password) {
    errors.password = 'La contraseña es requerida'
  } else if (password.length < 8) {
    errors.password = 'La contraseña debe tener al menos 8 caracteres'
  } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    errors.password = 'La contraseña debe contener al menos una mayúscula, una minúscula y un número'
  }

  return errors
}

/** Atajo: ¿el alta se puede enviar? */
export function isRegistrationValid(input: RegistrationInput): boolean {
  return Object.keys(validateRegistration(input)).length === 0
}

// ============== NOMBRE A MOSTRAR ==============

/**
 * Cómo se llama quien ha entrado, resuelto en UN SOLO SITIO.
 *
 * Existe porque `UserResponse` NO tiene campo `name` —solo id, email y flags—
 * y media app lo daba por hecho: `authStore.user?.name` era siempre
 * `undefined`, así que la cabecera de Inicio pintaba un avatar con "?" y el
 * chat saludaba "Hola, familia" a todo el mundo. `ProfileView` de web ya se lo
 * había apañado por su cuenta casteando `user` a una forma que el DTO no
 * declara: tres pantallas, tres respuestas distintas a la misma pregunta.
 *
 * El nombre vive en la ficha de MIEMBRO, que el backend devuelve en
 * login/register y que ahora se persiste con la sesión.
 *
 * Orden: nombre y apellido del miembro → su `name` → la parte local del correo
 * (mejor "jose" que "Tú") → el respaldo que pida quien llama.
 */
export function displayNameOf(
  member: MemberResponse | null | undefined,
  user: UserResponse | null | undefined,
  fallback = ''
): string {
  const completo = [member?.firstName, member?.lastName].filter(Boolean).join(' ').trim()
  if (completo) return completo

  const nombre = member?.name?.trim()
  if (nombre) return nombre

  const correo = (member?.email || user?.email)?.trim()
  if (correo && correo.includes('@')) return correo.split('@')[0]

  return fallback
}

/** Solo el primer nombre, para saludos y para la inicial del avatar. */
export function firstNameOf(
  member: MemberResponse | null | undefined,
  user: UserResponse | null | undefined,
  fallback = ''
): string {
  const completo = displayNameOf(member, user, '')
  if (!completo) return fallback
  return completo.split(/\s+/)[0]
}
